import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Plus } from "lucide-react";
import { ApprovalRuleCard } from "@/components/approval/ApprovalRuleCard";
import { Button } from "@/components/ui/button";
import { PanelLoading } from "@/components/layout/AppLoading";
import { useErrorToast } from "@/hooks/use-error-toast";
import { useHasPermission } from "@/features/auth/useAuth";
import { PERMISSIONS } from "@/features/auth/permissions";
import { createRuleDraft } from "@/lib/approvalRules";
import {
  useGetApprovalRuleFieldsQuery,
  useGetApprovalRulesQuery,
} from "@/services/approvals/approvalsApi";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 4;

export function ApprovalRulesPage() {
  const showErrorToast = useErrorToast();
  // Reaching this page needs only "Get approval rules List" — a read permission.
  // Creating, editing and deleting a rule are three further grants, and none of
  // them was being checked, so view access amounted to delete access.
  const can = useHasPermission();
  const canCreate = can(PERMISSIONS.CREATE_APPROVAL_RULE);
  const canEdit = can(PERMISSIONS.UPDATE_APPROVAL_RULE);
  const canDelete = can(PERMISSIONS.DELETE_APPROVAL_RULE);
  const [pageNumber, setPageNumber] = useState(1);
  const [draft, setDraft] = useState(null);

  const {
    data: rules = [],
    isLoading,
    error: rulesError,
  } = useGetApprovalRulesQuery();
  // The field list only feeds the rule editor, so it is not fetched for someone
  // who cannot open one — the request would 403 on every visit to the page.
  const canReadFields = can(PERMISSIONS.APPROVAL_RULE_FIELDS);
  const { data: columns = [], error: fieldsError } = useGetApprovalRuleFieldsQuery(undefined, {
    skip: !canReadFields,
  });

  useEffect(() => {
    if (rulesError) showErrorToast(rulesError);
  }, [rulesError, showErrorToast]);

  useEffect(() => {
    if (fieldsError) showErrorToast(fieldsError);
  }, [fieldsError, showErrorToast]);

  const totalPages = Math.max(1, Math.ceil(rules.length / PAGE_SIZE));
  const currentPage = Math.min(pageNumber, totalPages);

  const pageRules = useMemo(
    () => rules.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [currentPage, rules],
  );

  // A new rule is a local draft until the server accepts it; the list itself is
  // server state and is never edited in place.
  const startDraft = () => {
    setDraft(createRuleDraft());
    setPageNumber(1);
  };

  if (isLoading) {
    return <PanelLoading label="Loading approval rules" />;
  }

  const isEmpty = rules.length === 0 && !draft;

  return (
    <div className="content-stack flex h-full min-h-0 flex-1 flex-col">
      <section className="page-panel">
        <div className="page-toolbar shrink-0 border-b border-line px-4 py-3 sm:px-5">
          {canCreate && (
            <Button
              type="button"
              className="h-9 rounded-[0.8rem]"
              disabled={Boolean(draft)}
              onClick={startDraft}
            >
              <Plus data-icon="inline-start" />
              Add rule
            </Button>
          )}
        </div>

        <div className="approval-rules-list">
          {draft && (
            <ApprovalRuleCard
              rule={draft}
              columns={columns}
              ruleNumber={rules.length + 1}
              onCancelDraft={() => setDraft(null)}
              canEdit={canEdit}
              canDelete={canDelete}
            />
          )}

          {pageRules.map((rule, index) => (
            <ApprovalRuleCard
              key={rule.guid}
              rule={rule}
              columns={columns}
              ruleNumber={rule.ruleNumber ?? (currentPage - 1) * PAGE_SIZE + index + 1}
              canEdit={canEdit}
              canDelete={canDelete}
            />
          ))}

          {isEmpty && (
            <div className="approval-rules-empty" role="status">
              <p className="text-sm font-semibold text-foreground">No rules yet</p>
              <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                Create a rule to automatically route invoices into the approval queue.
              </p>
              {canCreate && (
                <Button type="button" className="mt-4 h-9 rounded-[0.8rem]" onClick={startDraft}>
                  <Plus data-icon="inline-start" />
                  Add your first rule
                </Button>
              )}
            </div>
          )}
        </div>

        {rules.length > 0 && (
          <div className="page-footer">
            <span className="num text-sm" aria-live="polite">
              {rules.length} rule{rules.length === 1 ? "" : "s"} · Page {currentPage}/{totalPages}
            </span>
            <nav aria-label="Pagination" className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className={cn("touch-target-icon hidden bg-muted shadow-none sm:inline-flex")}
                disabled={currentPage <= 1}
                onClick={() => setPageNumber(1)}
                aria-label="First page"
              >
                <ChevronsLeft />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="touch-target-icon bg-muted shadow-none"
                disabled={currentPage <= 1}
                onClick={() => setPageNumber((page) => page - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="touch-target-icon bg-muted shadow-none"
                disabled={currentPage >= totalPages}
                onClick={() => setPageNumber((page) => page + 1)}
                aria-label="Next page"
              >
                <ChevronRight />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className={cn("touch-target-icon hidden bg-muted shadow-none sm:inline-flex")}
                disabled={currentPage >= totalPages}
                onClick={() => setPageNumber(totalPages)}
                aria-label="Last page"
              >
                <ChevronsRight />
              </Button>
            </nav>
          </div>
        )}
      </section>
    </div>
  );
}
